import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoIosNotificationsOutline } from "react-icons/io";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import NotificationItem from "../notification/NotificationItem";
import useInboxUnread, { inboxChanged, refreshUnread } from "../../hook/useInboxUnread";
import { listInbox, markAllRead, markRead } from "../../api/notificationInbox";
import { openNotice } from "../../utils/notificationLinks";

// The bell in the top bar (ticket 2.8.1). The badge is the unread count: hidden at 0, "9+" above nine. Clicking the bell opens a
// panel with the 10 newest notices and a "See all" link to the Notifications page. A failed request never breaks the top bar.

const PANEL_SIZE = 10;

const InboxPanel = ({ close }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState({});
  const alive = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await listInbox({ filter: "all", limit: PANEL_SIZE });
      if (!alive.current) return;
      setItems(result.items || []);
      inboxChanged(result.unread_count);
    } catch (e) {
      if (alive.current) setError(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const open = async (item) => {
    if (!item.is_read) {
      setItems((list) => list.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      markRead(item.id).then((r) => inboxChanged(r.unread_count)).catch(() => refreshUnread());
    }
    const result = await openNotice(item, navigate);
    if (result.opened) close();
    else if (result.message) setUnavailable((u) => ({ ...u, [item.id]: result.message }));
  };

  const readAll = async () => {
    setItems((list) => list.map((n) => ({ ...n, is_read: true })));
    try {
      const r = await markAllRead();
      inboxChanged(r.unread_count);
    } catch (e) {
      load();
      refreshUnread();
    }
  };

  const unread = items.some((n) => !n.is_read);

  return (
    <div className="flex max-h-[28rem] flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Notifications</h2>
        <button
          type="button"
          onClick={readAll}
          disabled={!unread}
          className="text-xs font-semibold text-green-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded"
        >
          Mark all as read
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <p className="p-4 text-sm text-gray-500" role="status">Loading...</p>
        ) : error ? (
          <div className="p-4 text-sm text-gray-600" role="alert">
            <p>We couldn't load your notifications.</p>
            <button type="button" onClick={load} className="mt-2 rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600">
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">You're all caught up. New notifications will show here.</p>
        ) : (
          <ul>
            {items.map((item) => (
              <NotificationItem key={item.id} item={item} compact onOpen={open} unavailable={unavailable[item.id]} />
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-gray-200 px-4 py-2 text-center dark:border-gray-700">
        <Link to="/Notification" onClick={close} className="text-sm font-semibold text-red-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded">
          See all
        </Link>
      </div>
    </div>
  );
};

const Notification = () => {
  const { count } = useInboxUnread();
  const [announce, setAnnounce] = useState("");
  const last = useRef(null);

  // Tell screen readers when the number changes (not on the first load).
  useEffect(() => {
    if (count === null) return;
    if (last.current !== null && last.current !== count) {
      setAnnounce(count === 0 ? "No unread notifications" : `${count} unread notification${count === 1 ? "" : "s"}`);
    }
    last.current = count;
  }, [count]);

  const shown = count > 9 ? "9+" : String(count);
  const label = count > 0 ? `Notifications, ${count} unread` : "Notifications";

  return (
    <div className="relative">
      <Popover className="relative inline-block text-left">
        <PopoverButton
          aria-label={label}
          className="relative ml-2 inline-flex items-center rounded-full p-2 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
        >
          <IoIosNotificationsOutline className="text-[40px] text-gray-700" aria-hidden="true" />
          {count > 0 && (
            <span aria-hidden="true" className="absolute -top-1 -right-1 min-w-[1.25rem] rounded-full border border-white bg-green-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white dark:border-gray-900">
              {shown}
            </span>
          )}
        </PopoverButton>
        <span role="status" aria-live="polite" className="sr-only">{announce}</span>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel className="absolute right-0 z-40 mt-0 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-800 lg:left-0 lg:right-auto max-lg:fixed max-lg:left-3 max-lg:right-3 max-lg:w-auto max-lg:max-w-none">
            {({ close }) => <InboxPanel close={close} />}
          </PopoverPanel>
        </Transition>
      </Popover>
    </div>
  );
};

export default Notification;
